/**
 * GxRoundGuide: the round-steps checklist at the foot of the rail. The full list shows each step done, current or next; where the rail is
 * narrow or the window short, shell.css swaps it for a progress line with the current step. The current step links to its screen.
 */
import { useState } from "react";
import { Icon } from "../theme/icons";
import type { RoundGuide } from "./guide";
import { Link } from "./history";
import "./round-guide.css";

export function GxRoundGuide({ guide }: { readonly guide: RoundGuide }) {
  const { steps, current } = guide;
  const now = steps[current] ?? steps[steps.length - 1];
  // The count is the steps done, not the place of the current one: a step that comes back (a thesis line once a lever changes) never makes the number step backward past what was done.
  const done = steps.filter((s) => s.status === "done" || s.counted === true).length;
  const position = current < 0 ? "Done" : `${done} of ${steps.length} done`;
  // The rail is 179 px wide: "2 of 11 done" fits on the header's one line, "10 of 11 done" does not, so the word goes first when the count is the longer one.
  const count = `${done} of ${steps.length}`;
  const heading = current < 0 ? "Done" : count.length <= 7 ? `${count} done` : count;
  // A window under 760 px high has no room for the list under the nav: it shows one line and opens the list in a popover.
  const [open, setOpen] = useState(false);
  return (
    <section className="gx-guide" aria-label="Round steps" data-testid="shell.guide">
      <h6 className="gx-eyebrow gx-rail-h gx-guide-h">Your round<span>{heading}</span></h6>
      <button type="button" className="gx-guide-short" aria-expanded={open} data-testid="shell.guide.short" onClick={() => setOpen((o) => !o)}>
        <span>{current < 0 ? "Your round: done" : `${position}: ${now?.label ?? ""}`}</span><span aria-hidden="true">{open ? "▾" : "▴"}</span>
      </button>
      <GxGuideList guide={guide} open={open} />
      {now === undefined ? null : (
        <Link href={now.href} className="gx-guide-line" data-testid="shell.guide.line" title={now.label}>
          <span className="gx-guide-bar" aria-hidden="true">{steps.map((s) => <i key={s.key} data-status={s.status} />)}</span>
          <small>{position}</small>
          <span>{now.label}</span>
        </Link>
      )}
    </section>
  );
}

/** The steps as links, each done, current or next. The rail shows it on a roomy window; the phone menu sheet shows it always (`onNavigate` closes the sheet). */
export function GxGuideList({ guide, open = false, sheet = false, onNavigate }: { readonly guide: RoundGuide; readonly open?: boolean; readonly sheet?: boolean; readonly onNavigate?: () => void }) {
  return (
    <ol className={sheet ? "gx-guide-list gx-guide-list--sheet" : "gx-guide-list"} data-open={open}>
      {guide.steps.map((s) => (
        <li key={s.key}>
          <Link href={s.href} className="gx-guide-step" data-status={s.status} data-testid={`shell.guide.${s.key}`} aria-current={s.status === "current" ? "step" : undefined} {...(onNavigate === undefined ? {} : { onClick: onNavigate })}>
            <span className="gx-guide-dot" aria-hidden="true">{s.status === "done" ? <Icon name="check" size={10} /> : null}</span>
            <span className="gx-guide-text">{s.label}{s.hint === null ? null : <small>{s.hint}</small>}</span>
            {s.status === "done" ? <span className="gx-sr">, done</span> : null}
          </Link>
        </li>
      ))}
    </ol>
  );
}

/** On a phone (shell.css shows it at 768 px and below): one row under the top bar naming the step in front of the player, with the round's progress; it links to that step's screen. */
export function GxGuideStrip({ guide }: { readonly guide: RoundGuide }) {
  const { steps, current } = guide;
  const now = steps[current];
  const done = steps.filter((s) => s.status === "done" || s.counted === true).length;
  if (now === undefined && current >= 0) return null;
  return (
    <Link href={now?.href ?? steps[steps.length - 1]?.href ?? "#"} className="gx-guidestrip" data-testid="shell.guidestrip">
      <span className="gx-guide-bar" aria-hidden="true">{steps.map((s) => <i key={s.key} data-status={s.status} />)}</span>
      <span className="gx-guidestrip-t"><small>{current < 0 ? "Done" : `${done} of ${steps.length}`}</small><span>{now?.label ?? "Your round is done"}</span></span>
    </Link>
  );
}
